import { Text } from '@shared/components';
import { useExtensionMessagingContext } from '@shared/contexts/useExtensionMessagingContext';
import { LoadShipmentForm } from '@shared/features/load-shipment-form';
import { usePageInfo } from '@shared/features/sidekick-navigator/PageInfoContextProvider';
import {
  isTMSPageInfo,
  PageInfo,
  SupportedEntityType,
} from '@shared/features/sidekick-navigator/types';
import { SidekickSupportedTms } from '@shared/generated/graphql';
import { useOrganizationFeatureFlag } from '@shared/hooks/useOrganizationFeatureFlag';
import { makeElementClassNameFactory, makeRootClassName } from '@shared/utils';
import { FeatureFlagName } from 'clerk_common/types';
import { isNil } from 'lodash';
import { useEffect, useState } from 'react';
import { useSearchParams } from 'react-router-dom';
import { ShipmentCardContainer } from './ShipmentCard';

const ROOT = makeRootClassName('TMSSidekick');
const el = makeElementClassNameFactory(ROOT);

export function TMSSidekick() {
  const { pageInfo } = usePageInfo();
  const { parentUrl } = useExtensionMessagingContext();
  const [searchParams] = useSearchParams();
  const [shipmentReference, setShipmentReference] = useState<string | null>(
    searchParams.get('shipmentReference')
  );
  const isLoadShipmentByIdEnabled = useOrganizationFeatureFlag({
    featureFlagName: FeatureFlagName.LOAD_SHIPMENT_BY_ID,
  });

  useEffect(() => {
    const pageReferenceNumber = toReferenceNumber(pageInfo);
    const urlShipment = searchParams.get('shipmentReference');

    // NOTE(max): At least on Turvo, it's confusing that if you switch to another
    // shipment, but the page is still the summary page and not the details
    // page, we won't load in the new shipment. We should add some more TMS-specific
    // UX for users to understand how to get a shipment loaded.
    if (
      !isNil(pageReferenceNumber) &&
      pageReferenceNumber !== shipmentReference
    ) {
      setShipmentReference(pageReferenceNumber);
    } else if (!isNil(urlShipment) && urlShipment !== shipmentReference) {
      setShipmentReference(urlShipment);
    }
  }, [pageInfo, searchParams]);

  return (
    <div className={ROOT}>
      <div className={el`shadow`} />
      {isLoadShipmentByIdEnabled && (
        <div className="flex h-[46px] w-full flex-col items-center bg-gray-50">
          <LoadShipmentForm setShipmentReference={setShipmentReference} />
        </div>
      )}
      <div className={el`shadow`} />
      <div className={el`content`}>
        <div className={el`tms-content`}>
          {shipmentReference ? (
            <ShipmentCardContainer
              parentUrl={parentUrl}
              referenceNumber={shipmentReference}
            />
          ) : (
            <NonShipmentPage />
          )}
        </div>
      </div>
    </div>
  );
}

function NonShipmentPage() {
  return (
    <div className="flex h-full w-full flex-col items-center justify-center">
      <Text type="body-sm">Open a shipment page to get started...</Text>
    </div>
  );
}

const toReferenceNumber = (pageInfo: PageInfo | null) => {
  if (isNil(pageInfo)) return null;
  if (!isTMSPageInfo(pageInfo)) return null;
  const shipmentEntity = pageInfo.entityInfo.find(
    (e) => e.entityType === SupportedEntityType.SHIPMENT
  );
  if (isNil(shipmentEntity)) return null;
  return shipmentEntity.entityReference;
};

export const toTMS = (pageInfo: PageInfo | null) => {
  if (isNil(pageInfo)) return SidekickSupportedTms.UNKNOWN;
  if (!isTMSPageInfo(pageInfo)) return SidekickSupportedTms.UNKNOWN;
  return pageInfo.tms;
};
